import axios from '@/api/provider';

/**
 * Transform portfolio from API snake_case to camelCase
 */
const transformPortfolio = (portfolio) => ({
  id: portfolio.id,
  name: portfolio.name,
  description: portfolio.description || null,
  isDefault: portfolio.is_default || false,
  companyCount: portfolio.company_count || 0,
  userCount: portfolio.user_count || 0,
  role: portfolio.role || 'member',
  assignedAt: portfolio.assigned_at || null,
  createdAt: portfolio.created_at || null,
  updatedAt: portfolio.updated_at || null
});

/**
 * Transform company in portfolio from API
 */
const transformCompany = (company) => ({
  id: company.id,
  displayName: company.display_name,
  companyIdentifier: company.company_identifier,
  region: company.region || null,
  addedAt: company.added_at || null
});

/**
 * Transform user in portfolio from API
 */
const transformUser = (user) => ({
  id: user.id,
  username: user.username,
  email: user.email,
  role: user.role,
  assignedAt: user.assigned_at || null
});

// ============================================================================
// PORTFOLIO CRUD
// ============================================================================

/**
 * List user's portfolios
 * Admin users can pass allPortfolios: true to see all system portfolios
 * @param {Object} options - { allPortfolios: boolean }
 * @returns {Promise<Object>} { success, portfolios }
 */
const listPortfolios = async (options = {}) => {
  const params = {};
  if (options.allPortfolios) {
    params.all_portfolios = true;
  }

  return axios
    .get('portfolios', { params })
    .then((response) => ({
      success: true,
      portfolios: (response || []).map(transformPortfolio)
    }))
    .catch((error) => {
      console.error('List portfolios error:', error);
      return {
        success: false,
        portfolios: [],
        error: error.message || 'Failed to load portfolios'
      };
    });
};

/**
 * Get portfolio details
 * @param {number} portfolioId - Portfolio ID
 * @returns {Promise<Object>} { success, portfolio }
 */
const getPortfolio = async (portfolioId) => {
  return axios
    .get(`portfolios/${portfolioId}`)
    .then((response) => ({
      success: true,
      portfolio: transformPortfolio(response)
    }))
    .catch((error) => {
      console.error('Get portfolio error:', error);
      throw error;
    });
};

/**
 * Create a new portfolio
 * @param {Object} data - { name, description }
 * @returns {Promise<Object>} { success, portfolio }
 */
const createPortfolio = async (data) => {
  return axios
    .post('portfolios', {
      name: data.name,
      description: data.description || null
    })
    .then((response) => ({
      success: true,
      portfolio: transformPortfolio(response)
    }))
    .catch((error) => {
      console.error('Create portfolio error:', error);
      throw error;
    });
};

/**
 * Update a portfolio
 * @param {number} portfolioId - Portfolio ID
 * @param {Object} data - { name, description }
 * @returns {Promise<Object>} { success, portfolio }
 */
const updatePortfolio = async (portfolioId, data) => {
  const payload = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.description !== undefined) payload.description = data.description;

  return axios
    .put(`portfolios/${portfolioId}`, payload)
    .then((response) => ({
      success: true,
      portfolio: transformPortfolio(response)
    }))
    .catch((error) => {
      console.error('Update portfolio error:', error);
      throw error;
    });
};

/**
 * Delete a portfolio
 * Cannot delete the default portfolio
 * @param {number} portfolioId - Portfolio ID
 * @returns {Promise<Object>} { success, portfolioId }
 */
const deletePortfolio = async (portfolioId) => {
  return axios
    .get(`portfolios/${portfolioId}`) // First get to confirm exists
    .then(() => {
      // Use raw axios delete since del() expects path + id format
      const nuxtApp = useNuxtApp();
      const token = localStorage.getItem('token');
      return nuxtApp.$api.delete(`portfolios/${portfolioId}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
    })
    .then(() => ({
      success: true,
      portfolioId
    }))
    .catch((error) => {
      console.error('Delete portfolio error:', error);
      throw error;
    });
};

// ============================================================================
// ACTIVE PORTFOLIO MANAGEMENT
// ============================================================================

/**
 * Get user's active portfolio
 * @returns {Promise<Object>} { success, portfolioId, portfolioName }
 */
const getActivePortfolio = async () => {
  return axios
    .get('portfolios/active')
    .then((response) => ({
      success: true,
      portfolioId: response.portfolio_id || null,
      portfolioName: response.portfolio_name || null
    }))
    .catch((error) => {
      console.error('Get active portfolio error:', error);
      return {
        success: false,
        portfolioId: null,
        portfolioName: null
      };
    });
};

/**
 * Set user's active portfolio
 * @param {number} portfolioId - Portfolio ID to activate
 * @returns {Promise<Object>} { success, portfolioId, portfolioName }
 */
const setActivePortfolio = async (portfolioId) => {
  return axios
    .put(`portfolios/active/${portfolioId}`, {})
    .then((response) => ({
      success: true,
      portfolioId: response.portfolio_id,
      portfolioName: response.portfolio_name
    }))
    .catch((error) => {
      console.error('Set active portfolio error:', error);
      throw error;
    });
};

// ============================================================================
// COMPANY MANAGEMENT
// ============================================================================

/**
 * List companies in a portfolio
 * @param {number} portfolioId - Portfolio ID
 * @returns {Promise<Object>} { success, companies }
 */
const listPortfolioCompanies = async (portfolioId) => {
  return axios
    .get(`portfolios/${portfolioId}/companies`)
    .then((response) => ({
      success: true,
      companies: (response || []).map(transformCompany)
    }))
    .catch((error) => {
      console.error('List portfolio companies error:', error);
      return {
        success: false,
        companies: []
      };
    });
};

/**
 * Add company to portfolio
 * @param {number} portfolioId - Portfolio ID
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} { success, portfolioId, companyId }
 */
const addCompanyToPortfolio = async (portfolioId, companyId) => {
  return axios
    .post(`portfolios/${portfolioId}/companies/${companyId}`, {})
    .then((response) => ({
      success: true,
      portfolioId: response.portfolio_id,
      companyId: response.company_id
    }))
    .catch((error) => {
      console.error('Add company to portfolio error:', error);
      throw error;
    });
};

/**
 * Remove company from portfolio
 * @param {number} portfolioId - Portfolio ID
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} { success, portfolioId, companyId }
 */
const removeCompanyFromPortfolio = async (portfolioId, companyId) => {
  const nuxtApp = useNuxtApp();
  const token = localStorage.getItem('token');

  return nuxtApp.$api
    .delete(`portfolios/${portfolioId}/companies/${companyId}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined }
    })
    .then((response) => ({
      success: true,
      portfolioId: response.data?.portfolio_id || portfolioId,
      companyId: response.data?.company_id || companyId
    }))
    .catch((error) => {
      console.error('Remove company from portfolio error:', error);
      throw error;
    });
};

// ============================================================================
// USER MANAGEMENT
// ============================================================================

/**
 * List users in a portfolio
 * @param {number} portfolioId - Portfolio ID
 * @returns {Promise<Object>} { success, users }
 */
const listPortfolioUsers = async (portfolioId) => {
  return axios
    .get(`portfolios/${portfolioId}/users`)
    .then((response) => ({
      success: true,
      users: (response || []).map(transformUser)
    }))
    .catch((error) => {
      console.error('List portfolio users error:', error);
      return {
        success: false,
        users: []
      };
    });
};

/**
 * Make a portfolio the default one — where newly created users are placed.
 *
 * It is three behaviours, not one, and the page says so before you press it:
 * new users (admin-created and self-signup) land here; an imported company
 * with no target and no active portfolio falls back to it; and it cannot be
 * deleted while it holds the flag. Only one portfolio can hold it, so this
 * also clears whichever one did.
 *
 * @param {number} portfolioId - Portfolio ID
 * @returns {Promise<Object>} The updated portfolio, camelCased
 */
const setDefaultPortfolio = async (portfolioId) => {
  return axios
    .put(`portfolios/${portfolioId}/default`)
    .then((response) => transformPortfolio(response))
    .catch((error) => {
      console.error('Set default portfolio error:', error);
      throw error;
    });
};

/**
 * Add user to portfolio
 * @param {number} portfolioId - Portfolio ID
 * @param {number} userId - User ID
 * @param {string} role - 'owner' or 'member'
 * @returns {Promise<Object>} { success, portfolioId, userId, role }
 */
const addUserToPortfolio = async (portfolioId, userId, role = 'member') => {
  return axios
    .post(`portfolios/${portfolioId}/users/${userId}`, { role })
    .then((response) => ({
      success: true,
      portfolioId: response.portfolio_id,
      userId: response.user_id,
      role: response.role
    }))
    .catch((error) => {
      console.error('Add user to portfolio error:', error);
      throw error;
    });
};

/**
 * Update user role in portfolio
 * @param {number} portfolioId - Portfolio ID
 * @param {number} userId - User ID
 * @param {string} role - 'owner' or 'member'
 * @returns {Promise<Object>} { success, portfolioId, userId, role }
 */
const updateUserRole = async (portfolioId, userId, role) => {
  return axios
    .put(`portfolios/${portfolioId}/users/${userId}`, { role })
    .then((response) => ({
      success: true,
      portfolioId: response.portfolio_id,
      userId: response.user_id,
      role: response.role
    }))
    .catch((error) => {
      console.error('Update user role error:', error);
      throw error;
    });
};

/**
 * Remove user from portfolio
 * @param {number} portfolioId - Portfolio ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} { success, portfolioId, userId }
 */
const removeUserFromPortfolio = async (portfolioId, userId) => {
  const nuxtApp = useNuxtApp();
  const token = localStorage.getItem('token');

  return nuxtApp.$api
    .delete(`portfolios/${portfolioId}/users/${userId}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined }
    })
    .then((response) => ({
      success: true,
      portfolioId: response.data?.portfolio_id || portfolioId,
      userId: response.data?.user_id || userId
    }))
    .catch((error) => {
      console.error('Remove user from portfolio error:', error);
      throw error;
    });
};

/**
 * Setup state for every company in a portfolio — the Company setup tab, rolled up.
 *
 * Passed through verbatim: the shape is already the page's (one row per company,
 * each carrying its six areas in display order), and renaming the keys here would
 * put a second vocabulary between the setup tab and the page that rolls it up.
 */
// ⚠ `axios` here is api/provider.js, not raw axios: its `handleResponse` has
// already returned `response.data`, so what arrives is the payload itself.
// Reading `.data` off it again yields undefined — the server answers 200 and
// the page throws on the next property access, which reads as "could not be
// loaded" with nothing wrong on the server.
const getReadiness = async (portfolioId = null) =>
  axios
    .get('portfolios/readiness', { params: portfolioId ? { portfolio_id: portfolioId } : {} })
    .catch((error) => {
      console.error('Portfolio readiness error:', error);
      throw error;
    });

export default {
  // CRUD
  listPortfolios,
  getReadiness,
  getPortfolio,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
  // Active portfolio
  getActivePortfolio,
  setActivePortfolio,
  // Default portfolio (where new users land)
  setDefaultPortfolio,
  // Company management
  listPortfolioCompanies,
  addCompanyToPortfolio,
  removeCompanyFromPortfolio,
  // User management
  listPortfolioUsers,
  addUserToPortfolio,
  updateUserRole,
  removeUserFromPortfolio
};
